import type { WorkspaceTaskBlockPaymentContext } from '@/cms/components/blocks/workspace/workspace-1/contracts/task-block-payment';
import type { SiteRuntimePricingPageLoadRequest } from '@/site-runtime/actions/load-site-runtime-pricing-page-body';
import type { AdminV2PaywallPopupBranchSlug } from '@/site-runtime/code-owned/paywall-branches';
import type { ComponentType } from 'react';
import type {
  SiteRuntimeLoginRequest,
  SiteRuntimePopupPreviewEditTargets,
} from './site-runtime-entry-action-controller';

export type SiteRuntimeLoginDialogRuntimeProps = {
  editTargets?: SiteRuntimePopupPreviewEditTargets;
  request: SiteRuntimeLoginRequest;
};

export type SiteRuntimePaywallDialogRuntimeProps = {
  branchSlug: AdminV2PaywallPopupBranchSlug;
  taskBlockPayment?: WorkspaceTaskBlockPaymentContext;
};

export type SiteRuntimeTaskPaymentDialogRuntimeProps = {
  context: WorkspaceTaskBlockPaymentContext;
};

let loginDialogRuntimePromise:
  | Promise<ComponentType<SiteRuntimeLoginDialogRuntimeProps>>
  | undefined;
let loadedLoginDialogRuntime:
  | ComponentType<SiteRuntimeLoginDialogRuntimeProps>
  | undefined;
let paywallDialogRuntimeModulePromise:
  | Promise<typeof import('./site-runtime-paywall-dialog-runtime.client')>
  | undefined;
let loadedPaywallDialogRuntime:
  | ComponentType<SiteRuntimePaywallDialogRuntimeProps>
  | undefined;
let taskPaymentDialogRuntimePromise:
  | Promise<typeof import('./site-runtime-task-payment-dialog-runtime.client')>
  | undefined;
let loadedTaskPaymentDialogRuntime:
  | ComponentType<SiteRuntimeTaskPaymentDialogRuntimeProps>
  | undefined;

export function readLoadedSiteRuntimeLoginDialogRuntime() {
  return loadedLoginDialogRuntime;
}

export function readLoadedSiteRuntimePaywallDialogRuntime() {
  return loadedPaywallDialogRuntime;
}

export function readLoadedSiteRuntimeTaskPaymentDialogRuntime() {
  return loadedTaskPaymentDialogRuntime;
}

export function loadSiteRuntimeLoginDialogRuntime() {
  loginDialogRuntimePromise ??= import(
    './site-runtime-login-dialog-runtime.client'
  )
    .then((module) => {
      loadedLoginDialogRuntime = module.SiteRuntimeLoginDialog;
      return loadedLoginDialogRuntime;
    })
    .catch((error: unknown) => {
      loadedLoginDialogRuntime = undefined;
      loginDialogRuntimePromise = undefined;
      throw error;
    });
  return loginDialogRuntimePromise;
}

function loadSiteRuntimePaywallDialogRuntimeModule() {
  paywallDialogRuntimeModulePromise ??= import(
    './site-runtime-paywall-dialog-runtime.client'
  )
    .then((module) => {
      loadedPaywallDialogRuntime = module.SiteRuntimePaywallDialog;
      return module;
    })
    .catch((error: unknown) => {
      loadedPaywallDialogRuntime = undefined;
      paywallDialogRuntimeModulePromise = undefined;
      throw error;
    });
  return paywallDialogRuntimeModulePromise;
}

export function loadSiteRuntimePaywallDialogRuntime() {
  return loadSiteRuntimePaywallDialogRuntimeModule().then(
    (module) => module.SiteRuntimePaywallDialog
  );
}

export function preloadSiteRuntimePaywallDialogRuntime(
  request: SiteRuntimePricingPageLoadRequest
) {
  return loadSiteRuntimePaywallDialogRuntimeModule().then((module) =>
    module.preloadSiteRuntimePricingPage(request)
  );
}

function loadSiteRuntimeTaskPaymentDialogRuntimeModule() {
  taskPaymentDialogRuntimePromise ??= import(
    './site-runtime-task-payment-dialog-runtime.client'
  )
    .then((module) => {
      loadedTaskPaymentDialogRuntime = module.SiteRuntimeTaskPaymentDialog;
      return module;
    })
    .catch((error: unknown) => {
      loadedTaskPaymentDialogRuntime = undefined;
      taskPaymentDialogRuntimePromise = undefined;
      throw error;
    });
  return taskPaymentDialogRuntimePromise;
}

export function loadSiteRuntimeTaskPaymentDialogRuntime() {
  return loadSiteRuntimeTaskPaymentDialogRuntimeModule().then(
    (module) => module.SiteRuntimeTaskPaymentDialog
  );
}
